import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";

import get from "lodash-es/get.js";
import type { FC } from "react";
import {
  useHref,
  useLocation,
  useNavigate,
  useOutlet,
  type To,
} from "react-router";

//
// Hook to manage history when navigating to routed modals.
//

/** Coordinates the open/closing of a major modal (such as a slideover) with the window history (browser back button). */
export function useModalNavigation() {
  const location = useLocation();
  const locationPathname = location.pathname;
  const componentPathname = useHref(".");
  const isOpen =
    locationPathname.startsWith(componentPathname) &&
    locationPathname !== componentPathname;

  const navigate = useNavigate();

  const push = (to: To, extraState?: Record<string, unknown>) => {
    navigate(to, {
      state: {
        // Picked up by `useNavigationScroll`
        isModal: true,
        canGoBack: true,
        ...extraState,
      },
    });
  };

  const pop = () => {
    if (get(location.state, "canGoBack") === true) {
      navigate(-1);
    } else if (isOpen) {
      navigate(".", { replace: true });
    } else {
      navigate("..", { replace: true });
    }
  };

  const onOpenChange = (newIsOpen: boolean) => {
    if (newIsOpen) {
      return;
    }

    pop();
  };

  const slideoverProps = { isDismissable: true, isOpen, onOpenChange };
  return { push, pop, slideoverProps };
}

//
// Navigation-aware slideover component.
//

interface NavigationSlideoverContainerProps {
  // If defined, redirect to this url when slideover closes.
  closeUrl?: string;
}

export const NavigationSlideoverContainer: FC<
  NavigationSlideoverContainerProps
> = ({ closeUrl }) => {
  const { isOpen, onOpenChange } = useModalNavigation().slideoverProps;
  const navigate = useNavigate();
  const outlet = useOutlet();

  const handleOpenChange = (newIsOpen: boolean) => {
    if (newIsOpen) return;

    if (closeUrl) {
      navigate(closeUrl, { replace: true });
    } else {
      onOpenChange(false);
    }
  };

  return (
    <SlideoverContainer
      isDismissable
      isOpen={isOpen}
      onOpenChange={closeUrl ? handleOpenChange : onOpenChange}
    >
      {outlet}
    </SlideoverContainer>
  );
};
